import { clsx } from 'clsx'
import { useState } from 'react'
import { NavLink, Link, Form } from 'react-router'
import logo from '#app/assets/publicare-akademie-logo.png'
import { ContactUsForm } from '#app/components/layout/contact-us-form.tsx'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '#app/components/ui/dialog.tsx'
import { UserDropdown } from '#app/components/user-dropdown.tsx'
import { userHasRole, useOptionalUser } from '#app/utils/user.ts'
import { ContactUs } from './contact-us.tsx'

export function Header() {
	const user = useOptionalUser()
	const hasAdminAccess = user
		? userHasRole(user, 'admin') || userHasRole(user, 'aussendienst')
		: false

	const isEventmanager = user ? userHasRole(user, 'eventmanager') : false
	const [isMenuOpen, setIsMenuOpen] = useState(false)
	const [open, setOpen] = useState(false)

	return (
		<header className="border-b-pcteal-700">
			<div className="mx-auto flex w-full max-w-screen-2xl items-center justify-between px-4 py-2">
				<h1 className="flex items-center gap-2">
					<Link to="/">
						<img src={logo} alt="Publicare Logo" style={{ height: 60 }} />
					</Link>
				</h1>

				<div className="lg:hidden">
					<button
						onClick={() => setIsMenuOpen(!isMenuOpen)}
						className="hover:text-pcteal-700 text-gray-500 focus:outline-none"
					>
						<svg
							className="h-6 w-6"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							{isMenuOpen ? (
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M6 18L18 6M6 6l12 12"
								/>
							) : (
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M4 6h16M4 12h16M4 18h16"
								/>
							)}
						</svg>
					</button>
				</div>

				<nav className="hidden items-center gap-4 md:gap-8 lg:flex">
					{user && (
						<NavLink
							className={({ isActive }) =>
								clsx(
									'hover:text-pcteal-700 font-alumni text-clamp-sm md:text-clamp-md text-gray-500 uppercase',
									isActive && 'text-pcteal-default',
								)
							}
							to="/katalog"
						>
							Veranstaltungen
						</NavLink>
					)}
					{user && (
						<NavLink
							className={({ isActive }) =>
								clsx(
									'hover:text-pcteal-700 font-alumni text-clamp-sm md:text-clamp-md text-gray-500 uppercase',
									isActive && 'text-pcteal-default',
								)
							}
							to="/meine-veranstaltungen"
						>
							Meine Veranstaltungen
						</NavLink>
					)}
					{(isEventmanager || hasAdminAccess) && (
						<NavLink
							className={({ isActive }) =>
								clsx(
									'hover:text-pcteal-700 font-alumni text-clamp-sm md:text-clamp-md text-gray-500 uppercase',
									isActive && 'text-pcteal-default',
								)
							}
							to="/anwesenheit"
						>
							Anwesenheit
						</NavLink>
					)}
					{hasAdminAccess && (
						<NavLink
							className={({ isActive }) =>
								clsx(
									'hover:text-pcteal-700 font-alumni text-clamp-sm md:text-clamp-md text-gray-500 uppercase',
									isActive && 'text-pcteal-default',
								)
							}
							to="/admin"
						>
							Admin
						</NavLink>
					)}
					<Dialog open={open} onOpenChange={setOpen}>
						<DialogTrigger>
							<span
								className={clsx(
									'hover:text-pcteal-700 font-alumni text-clamp-sm md:text-clamp-md text-gray-500 uppercase',
								)}
							>
								Kontakt
							</span>
						</DialogTrigger>
						<DialogContent
							onInteractOutside={(e) => e.stopPropagation()}
							className="container mx-auto"
						>
							<DialogHeader>
								<DialogTitle className="text-left">
									Kontaktieren Sie uns
								</DialogTitle>
								<ContactUsForm setOpen={setOpen} />
							</DialogHeader>
						</DialogContent>
					</Dialog>
					<UserDropdown />
				</nav>

				<div
					className={`text-clamp-md absolute top-16 left-0 z-50 w-full bg-white text-right shadow-md transition-all duration-300 ease-in-out lg:hidden ${
						isMenuOpen
							? 'translate-y-0 transform opacity-100'
							: 'pointer-events-none -translate-y-2 transform opacity-0'
					}`}
				>
					<div className="flex flex-col px-4 pb-2">
						{user && (
							<NavLink
								className={({ isActive }) =>
									clsx(
										'hover:text-pcteal-700 font-alumni animate-in slide-in-from-right-2 text-gray-500 uppercase transition-colors delay-100 duration-200',
										isActive && 'text-pcteal-default',
									)
								}
								to="/katalog"
								onClick={() => setIsMenuOpen(false)}
							>
								Veranstaltungen
							</NavLink>
						)}
						{user && (
							<NavLink
								className={({ isActive }) =>
									clsx(
										'hover:text-pcteal-700 font-alumni animate-in slide-in-from-right-2 text-gray-500 uppercase transition-colors delay-150 duration-200',
										isActive && 'text-pcteal-default',
									)
								}
								to="/meine-veranstaltungen"
								onClick={() => setIsMenuOpen(false)}
							>
								Meine Veranstaltungen
							</NavLink>
						)}
						{(isEventmanager || hasAdminAccess) && (
							<NavLink
								onClick={() => setIsMenuOpen(false)}
								className={({ isActive }) =>
									clsx(
										'hover:text-pcteal-700 font-alumni animate-in slide-in-from-right-2 text-gray-500 uppercase transition-colors delay-200 duration-200',
										isActive && 'text-pcteal-default',
									)
								}
								to="/anwesenheit"
							>
								Anwesenheit
							</NavLink>
						)}
						{hasAdminAccess && (
							<NavLink
								className={({ isActive }) =>
									clsx(
										'hover:text-pcteal-700 font-alumni animate-in slide-in-from-right-2 text-gray-500 uppercase transition-colors delay-200 duration-200',
										isActive && 'text-pcteal-default',
									)
								}
								to="/admin"
								onClick={() => setIsMenuOpen(false)}
							>
								Admin
							</NavLink>
						)}
						{user && (
							<NavLink
								prefetch="intent"
								to="/profile"
								className={({ isActive }) =>
									clsx(
										'hover:text-pcteal-700 font-alumni animate-in slide-in-from-right-2 text-gray-500 uppercase transition-colors delay-100 duration-200',
										isActive && 'text-pcteal-default',
									)
								}
								onClick={() => setIsMenuOpen(false)}
							>
								Profil
							</NavLink>
						)}
						{user && (
							<Form
								action="/logout"
								method="POST"
								className="animate-in slide-in-from-right-2 delay-300 duration-200"
							>
								<button
									type="submit"
									className="hover:text-pcteal-700 font-alumni mt-4 text-gray-500 uppercase transition-colors duration-150"
								>
									Abmelden
								</button>
							</Form>
						)}
						<div className="">
							<ContactUs className="hover:text-pcteal-700 font-alumni animate-in slide-in-from-right-2 text-clamp-sm text-gray-500 uppercase transition-colors delay-100 duration-200" />
						</div>
						<NavLink
							to="/datenschutz"
							className={({ isActive }) =>
								clsx(
									'hover:text-pcteal-700 font-alumni animate-in slide-in-from-right-2 text-clamp-sm text-gray-500 uppercase transition-colors delay-100 duration-200',
									isActive && 'text-pcteal-default',
								)
							}
						>
							Datenschutz
						</NavLink>
						<NavLink
							to="/nutzungsbedingungen"
							className={({ isActive }) =>
								clsx(
									'hover:text-pcteal-700 font-alumni animate-in slide-in-from-right-2 text-clamp-sm text-gray-500 uppercase transition-colors delay-100 duration-200',
									isActive && 'text-pcteal-default',
								)
							}
						>
							Nutzungsbedingungen
						</NavLink>
						<NavLink
							to="/impressum"
							className={({ isActive }) =>
								clsx(
									'hover:text-pcteal-700 font-alumni animate-in slide-in-from-right-2 text-clamp-sm text-gray-500 uppercase transition-colors delay-100 duration-200',
									isActive && 'text-pcteal-default',
								)
							}
						>
							Impressum
						</NavLink>
					</div>
				</div>
			</div>
		</header>
	)
}
